import * as React from "react";
import jspreadsheet from "jspreadsheet";
import "jspreadsheet/dist/jspreadsheet.css";
import "styles/layout/jsuites.css";
import classNames from "classnames";
import { debounce } from "lodash";

jspreadsheet.setLicense(
  // eslint-disable-next-line max-len
  "NjQzNzlmNDM4OGM1ZmRkZjlhMWYzMmQ3NmFjODc5OTYyM2FhMDQxM2UzNTU3YzFjODRlMDk3YmRjYmYxODI2ZDY3YTk3ZGExNmQzYjZhOTQzZThjZjVjMTBkYWVmYzg1ZjA3ZTQwZjRlN2NkNWE1MTliOTY4MDljNzM5NjZjMzYsZXlKamJHbGxiblJKWkNJNklqTmhaV1E1WWpBek1UTm1PVEl5TmpFeE1XUmxPR0ZsWVdKaFpXUmpZMlk0WkdJd04yUTBNamdpTENKdVlXMWxJam9pVUUxSElGQnliMnBsYTNSeVlYVnRJRTFoYm1GblpXMWxiblFnUjIxaVNDSXNJbVJoZEdVaU9qRTNPVFl4TmprMk1EQXNJbVJ2YldGcGJpSTZXeUp3YldkcGJuUmxjbTR1WkdVaUxDSndiV2RwYm5SbGNtNHRkR1Z6ZEM1a1pTSXNJbkJ0WjJsdWRHVnliaTF6WTJoMWJIVnVaeTVrWlNJc0luQnliMnBsYTNSeVlYVnRMbVYxSWl3aWNHMW5MV1JoZEdWdWNtRjFiUzVrWlNJc0luQnRaeTF6YUdGeVpTNWtaU0lzSW10aFoyVnpMbkJoZG1VdVlYUWlMQ0p6ZDIxd2NtOXFaV3QwY21GMWJTNWtaU0lzSW1KaGVYZHZZbUYxTFhkbFltRnlZMmhwZGk1a1pTSXNJbU5rYzNCeWIycGxhM1J5WVhWdExtUmxJaXdpYkc5allXeG9iM04wSWwwc0luQnNZVzRpT2lJMUlpd2ljMk52Y0dVaU9sc2lkamNpTENKMk9DSXNJblk1SWl3aWRqRXdJaXdpZGpFeElpd2lkakV5SWwxOQ=="
);

/** part of the visible table height a vertical header is allowed to occupy */
const MAX_HEADER_HEIGHT_RATIO = 0.5;
/** height a single character of a vertical header takes, in px */
const VERTICAL_CHAR_HEIGHT = 7;
/** shortest title a vertical header keeps, even in a very small table */
const MIN_VERTICAL_HEADER_LENGTH = 15;
/** title length used as long as the table height is unknown */
const DEFAULT_VERTICAL_HEADER_LENGTH = 50;

export interface JspreadsheetCellUpdate<T> {
  col: string;
  x: number;
  row: string;
  y: number;
  oldValue: T;
  value: string;
}

interface JspreadsheetProps extends jspreadsheet.Spreadsheet {
  className?: string;
  stretchHeight?: boolean;
  headerWith90Degree?: number[];
  onTableClick?: (e) => void;
  headerTextColors?: string[];
  headerBackgroundColors?: string[];
  onHoverHeader?: (e: MouseEvent) => void;
  onHeaderClick?: (e: MouseEvent) => void;
  onHoverBody?: (e: MouseEvent) => void;
}

export class Jspreadsheet extends React.Component<JspreadsheetProps> {
  wrapper = React.createRef<HTMLDivElement>();
  instance: jspreadsheet.worksheetInstance;
  selectedCells: HTMLElement[] = [];
  selectedCellsPrevious: HTMLElement[] = [];
  private debounceSetMaxHeight: () => void;
  private debounceHoverBody: (e: MouseEvent) => void;
  private maxVerticalHeaderLength = DEFAULT_VERTICAL_HEADER_LENGTH;
  constructor(props) {
    super(props);
    this.debounceSetMaxHeight = debounce(this.handleSetMaxHeight, 500);
  }

  componentDidMount() {
    this.initialize();

    const container = document.querySelector<HTMLDivElement>(".jss-stretch");
    if (container) {
      this.handleSetMaxHeight();
      window.addEventListener("resize", this.debounceSetMaxHeight);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.debounceSetMaxHeight);
    document.querySelector<HTMLDivElement>(".jss_content")?.removeEventListener("mousemove", this.debounceHoverBody);
    if (this.props.onHoverHeader) {
      document
        .querySelector<HTMLDivElement>(".jss_content")
        ?.removeEventListener("mousemove", this.props.onHoverHeader);
    }
    if (this.props.onHeaderClick) {
      document.querySelector<HTMLDivElement>(".jss_content")?.removeEventListener("click", this.props.onHeaderClick);
    }
  }

  handleSetMaxHeight = () => {
    this.instance.setViewport(this.wrapper.current.offsetWidth - 10, this.wrapper.current.offsetHeight);
  };

  getTableData = (onlyHighlightedCells: boolean = false) => {
    return this.instance.getData(onlyHighlightedCells);
  };

  getSelectedColumns = () => {
    return this.instance.getSelectedColumns();
  };

  getCellFromCoords = (x: number, y: number) => {
    return this.instance.getCellFromCoords(x, y);
  };

  getCellValueFromCoords = (x: number, y: number) => {
    return this.instance.getValueFromCoords(x, y, undefined);
  };

  hideColumn = (colNr: number) => {
    return this.instance.hideColumn(colNr);
  };

  showColumn = (colNr: number) => {
    return this.instance.showColumn(colNr);
  };

  hideRow = (rowNr: number) => {
    this.instance.hideRow(rowNr);
  };

  showRow = (rowNr: number) => {
    this.instance.showRow(rowNr);
  };

  closeEditor = (obj, save) => {
    this.instance.closeEditor(obj, save);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setCellStyle = (cell: any, value: string, force?: boolean) => {
    this.instance.setStyle(cell, null, value, force);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setCellValue = (cell: any, value: string, force?: boolean) => {
    this.instance.setValue(cell, value, force);
  };

  setValueFromCoords: jspreadsheet.worksheetInstance["setValueFromCoords"] = (x, y, value, force) => {
    this.instance.setValueFromCoords(x, y, value, force);
  };

  updateSelectionFromCoords: jspreadsheet.worksheetInstance["updateSelectionFromCoords"] = (x1, y1, x2, y2, origin) => {
    this.instance.updateSelectionFromCoords(x1, y1, x2, y2, origin);
  };

  getSelectedCells = () => {
    return this.selectedCells;
  };

  getPreviousSelectedCells = () => {
    return this.selectedCellsPrevious;
  };

  onSelection: jspreadsheet.Spreadsheet["onselection"] = (instance, x1, y1, x2, y2, origin) => {
    for (const cell of this.selectedCells) cell?.removeAttribute("selected-range");
    this.selectedCellsPrevious = [...this.selectedCells];
    this.selectedCells = [];
    for (let i = x1; i <= x2; i++) {
      for (let j = y1; j <= y2; j++) {
        const cell = this.getCellFromCoords(i, j);
        this.selectedCells.push(cell);
        cell.setAttribute("selected-range", "1");
      }
    }
    if (this.props.onselection) this.props.onselection(instance, x1, y1, x2, y2, origin);
  };

  moveRow(from: number, to: number) {
    this.instance.moveRow(from, to);
  }

  deleteRow(rowNr: number) {
    this.instance.deleteRow(rowNr, 1);
  }

  /**
   * set the column widthes, that the hole content can be shown in one row
   * @param colNrs
   */
  autofitColumns(colNrs: number[] | string[]) {
    this.instance.autoWidth(colNrs.map((i) => +i));
  }

  /**
   * check, if a column doesn't contain values
   * @param colNr the number of the col
   * @returns
   */
  isColEmpty(colNr: number) {
    const colData = this.instance.getColumnData(colNr, true);
    for (let idx = 0; idx < colData.length; idx++) {
      if (colData[idx] !== "") {
        return false;
      }
    }
    return true;
  }

  addRow = (data?: Array<unknown>) => {
    return this.instance.insertRow(data);
  };

  setRowData = (row: string | number, data: unknown[]) => {
    this.instance.setRowData(+row, data, true);
  };

  getRowData = (row: string | number): string[] => {
    return this.instance.getRowData(+row, true);
  };

  addColumn: jspreadsheet.worksheetInstance["insertColumn"] = (
    values: number | unknown[],
    columnNumber?: number,
    insertBefore?: boolean,
    properties?
  ) => {
    this.instance.insertColumn(values, columnNumber, insertBefore, properties);
  };

  setTableData = (data: unknown[]) => {
    this.instance.setData(data);
  };

  handleCreateColumn: jspreadsheet.Spreadsheet["oncreatecolumn"] = (worksheet, columnNumber, td, _options) => {
    if (td.style.display === "none") {
      return;
    }

    if (this.props.headerWith90Degree) {
      if (td.style.display === "none") {
        return;
      }
      if (this.props.headerWith90Degree.includes(columnNumber)) {
        td.style.verticalAlign = "bottom";
        return;
      }

      const div = document.createElement("div");
      div.innerHTML = td.innerHTML;
      div.classList.add("rotated");

      const fullTitle = div.textContent;
      if (!div.children.length && fullTitle.length > this.maxVerticalHeaderLength) {
        div.textContent = fullTitle.slice(0, this.maxVerticalHeaderLength).trimEnd() + "…";
        td.title = fullTitle;
      }

      td.innerHTML = "";
      td.classList.add("text-vertical");
      td.appendChild(div);
    }
    if (this.props.headerBackgroundColors && this.props.headerBackgroundColors[columnNumber]) {
      td.style.backgroundColor = this.props.headerBackgroundColors[columnNumber];
    }
  };

  /**
   * a vertical header cell grows with every single character it contains, so a long title can push
   * the table body out of the visible area. Only as many characters as fit into the part of the
   * table the header may occupy are shown, the full title is available as tooltip.
   */
  updateMaxVerticalHeaderLength = () => {
    const tableHeight = this.wrapper.current.offsetHeight;
    if (!tableHeight) {
      return;
    }
    this.maxVerticalHeaderLength = Math.max(
      MIN_VERTICAL_HEADER_LENGTH,
      Math.round((tableHeight * MAX_HEADER_HEIGHT_RATIO) / VERTICAL_CHAR_HEIGHT)
    );
  };

  initialize = () => {
    this.updateMaxVerticalHeaderLength();

    const conf: JspreadsheetProps = {
      ...this.props,
      oncreatecolumn: this.handleCreateColumn,
      onselection: this.onSelection,
    };

    const worksheets = jspreadsheet(this.wrapper.current, conf);
    this.instance = worksheets?.[worksheets.length - 1];
    // sets the size of the jspreadsheet - has to be done for the virtualization of rows
    this.instance.setViewport(this.wrapper.current.offsetWidth, this.wrapper.current.offsetHeight);
    const container = document.querySelector<HTMLDivElement>(".jss_content");
    if (!container) {
      return;
    }

    if (this.props.onTableClick) {
      container.onclick = this.props.onTableClick;
    }
    if (this.props.onHoverBody) {
      this.debounceHoverBody = debounce(this.props.onHoverBody, 120);
      container.querySelector("tbody")?.addEventListener("mousemove", this.debounceHoverBody);
    }

    if (this.props.onHoverHeader) {
      container.querySelector("thead")?.addEventListener("mousemove", this.props.onHoverHeader);
    }

    if (this.props.onHeaderClick) {
      container.querySelector("thead")?.addEventListener("click", this.props.onHeaderClick);
    }

    if (this.props.headerTextColors) {
      const headers = this.instance.headers;
      if (headers.length) {
        for (const colIdx in this.props.headerTextColors) {
          const idx = +colIdx;
          if (headers[idx]) {
            headers[+colIdx].style.color = this.props.headerTextColors[colIdx];
          }
        }
      }
    }
  };

  /**
   * Update a rotated header column name. `setHeader` replaces the inner content
   * of the `<td>`, so the rotated `<div>` and truncation must be re-applied.
   * @param colNr the col number
   * @param newText the new text
   */
  setNew45DegreeHeader(colNr: number, newText: string) {
    this.instance.setHeader(colNr, newText);

    const td = this.instance.headers[colNr];
    if (!td) {
      return;
    }

    let displayText = newText;
    if (newText.length > this.maxVerticalHeaderLength) {
      displayText = newText.slice(0, this.maxVerticalHeaderLength).trimEnd() + "…";
      td.title = newText;
    } else {
      td.title = "";
    }

    const div = document.createElement("div");
    div.textContent = displayText;
    div.classList.add("rotated");

    td.innerHTML = "";
    td.classList.add("text-vertical");
    td.appendChild(div);
  }

  emitFakeUpdateTable = () => {
    if (!this.props.updateTable) {
      return;
    }
    const data = this.getTableData();
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < data[0].length; j++) {
        const cell = this.getCellFromCoords(j, i);
        this.props.updateTable(this.instance, cell, j, i, data[i][j]);
      }
    }
  };

  reInitialize = () => {
    jspreadsheet.destroy(this.instance.element, true);
    this.instance.deleteWorksheet(0);
    this.initialize();
  };

  render() {
    const { stretchHeight, className } = this.props;
    const wrapperClassName = classNames(className, { "jss-stretch": stretchHeight });
    return (
      <div className={wrapperClassName}>
        <div
          ref={this.wrapper}
          className="resizable-fix"
          style={{ height: "100%", overflow: "hidden", width: "100%" }}
        />
      </div>
    );
  }
}
